import { STATEMENT_ID } from './contract';

/**
 * The accessibility statement Edlio writes for the school, shown in a dialog.
 *
 * The server fills in the school's details and drops lines whose detail is
 * empty (AccessibilityWidgetStatement.render), then emits the finished text
 * as JSON. This file only turns that text into elements, with the same small
 * set of rules the admin page's preview uses (edlio/frontend
 * `src/utils/cms/accessibilityWidgetStatement.ts`):
 *
 *   - a line starting "## " is a heading
 *   - consecutive lines starting "- " are a list
 *   - other lines are paragraphs; a blank line ends one
 *   - email addresses, web addresses and phone numbers become links
 *
 * Everything is built with createElement and text nodes. The text never goes
 * near innerHTML, so nothing a school types can become markup.
 */

/**
 * The statement text, or null when the school links to its own page instead.
 * Only the element the server emits counts: a `<script type="application/json">`
 * with our id directly before the host. An element with the same id elsewhere
 * in the page content is ignored, so it cannot swap in its own text.
 */
export function readStatement(host: Element): string | null {
  const el = host.previousElementSibling;
  if (
    !el ||
    el.tagName !== 'SCRIPT' ||
    el.id !== STATEMENT_ID ||
    (el.getAttribute('type') || '').toLowerCase() !== 'application/json'
  )
    return null;
  try {
    const data = JSON.parse(el.textContent || '');
    return data && typeof data.text === 'string' && data.text.trim()
      ? data.text
      : null;
  } catch (e) {
    return null;
  }
}

type Block =
  | { kind: 'heading'; text: string }
  | { kind: 'list'; items: string[] }
  | { kind: 'paragraph'; lines: string[] };

export function parseStatement(text: string): Block[] {
  const blocks: Block[] = [];
  let current: Block | null = null;
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) {
      current = null;
      continue;
    }
    if (line.indexOf('## ') === 0) {
      blocks.push({ kind: 'heading', text: line.slice(3).trim() });
      current = null;
    } else if (line.indexOf('- ') === 0) {
      if (!current || current.kind !== 'list') {
        current = { kind: 'list', items: [] };
        blocks.push(current);
      }
      current.items.push(line.slice(2).trim());
    } else {
      if (!current || current.kind !== 'paragraph') {
        current = { kind: 'paragraph', lines: [] };
        blocks.push(current);
      }
      current.lines.push(line);
    }
  }
  return blocks;
}

const LINK =
  /([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})|(https?:\/\/[^\s<>"']+)|((?:\+?1[\s.-]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}(?:\s*(?:x|ext\.?)\s*\d{1,6})?)/g;

/** Text with email, web and phone links, as nodes appended to `parent`. */
export function appendLinked(doc: Document, parent: Node, text: string): void {
  let last = 0;
  let match: RegExpExecArray | null;
  LINK.lastIndex = 0;
  while ((match = LINK.exec(text))) {
    let value = match[0];
    let href: string;
    if (match[1]) {
      href = 'mailto:' + value;
    } else if (match[2]) {
      // Leave sentence punctuation outside the link.
      const trailing = /[.,;:!?)]+$/.exec(value);
      if (trailing) value = value.slice(0, -trailing[0].length);
      href = value;
    } else {
      href =
        'tel:' + value.replace(/(?:x|ext\.?).*$/i, '').replace(/[^\d+]/g, '');
    }
    if (match.index > last)
      parent.appendChild(doc.createTextNode(text.slice(last, match.index)));
    const a = doc.createElement('a');
    a.href = href;
    a.textContent = value;
    parent.appendChild(a);
    last = match.index + value.length;
    LINK.lastIndex = last;
  }
  if (last < text.length)
    parent.appendChild(doc.createTextNode(text.slice(last)));
}

export function renderStatement(doc: Document, text: string): DocumentFragment {
  const fragment = doc.createDocumentFragment();
  parseStatement(text).forEach((block) => {
    if (block.kind === 'heading') {
      const h = doc.createElement('h3');
      appendLinked(doc, h, block.text);
      fragment.appendChild(h);
    } else if (block.kind === 'list') {
      const ul = doc.createElement('ul');
      block.items.forEach((item) => {
        const li = doc.createElement('li');
        appendLinked(doc, li, item);
        ul.appendChild(li);
      });
      fragment.appendChild(ul);
    } else {
      const p = doc.createElement('p');
      block.lines.forEach((line, i) => {
        if (i > 0) p.appendChild(doc.createElement('br'));
        appendLinked(doc, p, line);
      });
      fragment.appendChild(p);
    }
  });
  return fragment;
}
